import { useState } from 'react';
import kit from './kit.module.css';
import styles from './VisibleMemoryDemo.module.css';

type MemoryId = 'table' | 'seafood';

interface Memory {
  id: MemoryId;
  options: string[];
  source: string;
  learned: string;
}

const MEMORIES: Memory[] = [
  { id: 'table', options: ['Table for 2', 'Table for 4'], source: 'from May bookings', learned: 'May 12' },
  { id: 'seafood', options: ['No seafood', 'No shellfish'], source: 'from May bookings', learned: 'May 12' },
];

// Facts the agent remembers but didn't use for this booking
const OTHER_FACTS = [
  { fact: 'Prefers 8 PM', source: 'from 6 bookings', learned: 'Apr - Sep' },
  { fact: 'Lives in Lisbon', source: 'from your profile', learned: 'Jan 3' },
];

const HELPER = 'Remove a memory to see how the booking changes.';

interface MemoryState {
  value: string;
  removed: boolean;
  edited: boolean;
}

export function VisibleMemoryDemo() {
  const [state, setState] = useState<Record<MemoryId, MemoryState>>({
    table: { value: 'Table for 2', removed: false, edited: false },
    seafood: { value: 'No seafood', removed: false, edited: false },
  });
  const [editing, setEditing] = useState<MemoryId | null>(null);
  const [showAll, setShowAll] = useState(false);
  const [feedback, setFeedback] = useState(HELPER);

  const noSeafood = !state.seafood.removed && state.seafood.value === 'No seafood';
  const restaurant = noSeafood ? 'Tasca do Rio' : 'Marisqueira Azul';
  const party = state.table.removed ? null : state.table.value.replace('Table for ', '');
  const removed = MEMORIES.filter((m) => state[m.id].removed).map((m) => state[m.id].value.toLowerCase());

  const remove = (id: MemoryId) => {
    setState((prev) => ({ ...prev, [id]: { ...prev[id], removed: true } }));
    setEditing(null);
    setFeedback(
      id === 'seafood'
        ? 'Removed at the source. The booking updates right away, and future bookings will include seafood places.'
        : "Removed. Without a remembered party size, the agent asks instead of guessing."
    );
  };

  const choose = (id: MemoryId, value: string) => {
    setState((prev) => ({ ...prev, [id]: { ...prev[id], value, edited: value !== prev[id].value || prev[id].edited } }));
    setEditing(null);
    setFeedback('Edited where it was shown - no trip to settings needed.');
  };

  return (
    <div className={kit.root}>
      <div className={kit.bubbleUser}>Book dinner for Friday.</div>

      <div className={kit.agent}>
        <span className={kit.agentIcon} aria-hidden="true">✦</span>
        <p>Booked. Here's what I used from memory.</p>
      </div>

      <div className={kit.proposal}>
        <div className={kit.proposalTop}>
          <span>Booking · Friday</span>
          <span>8 PM</span>
        </div>
        <h3 className={kit.proposalTitle}>{restaurant}</h3>
        <dl className={kit.fields}>
          <dt>Time</dt>
          <dd>Friday, 8 PM</dd>
          <dt>Party</dt>
          <dd>{party ? `${party} people` : 'Not set - I will ask before confirming'}</dd>
        </dl>
        {removed.length > 0 && (
          <p className={styles.note}>Updated without: {removed.join(', ')}</p>
        )}

        <div className={styles.memories}>
          <span className={kit.label}>Used from memory</span>
          {MEMORIES.filter((m) => !state[m.id].removed).map((m) => (
            <div key={m.id} className={styles.memory}>
              <div className={styles.memoryText}>
                <span className={kit.chip}>{state[m.id].value}</span>
                <span className={styles.source}>
                  {state[m.id].edited ? 'edited just now' : `${m.source} · ${m.learned}`}
                </span>
              </div>
              <div className={kit.row}>
                <button type="button" className={`${kit.btn} ${kit.btnEdit} ${styles.small}`} onClick={() => setEditing(editing === m.id ? null : m.id)}>
                  Edit
                </button>
                <button type="button" className={`${kit.btn} ${kit.btnGhost} ${styles.small}`} onClick={() => remove(m.id)}>
                  Remove
                </button>
              </div>
              {editing === m.id && (
                <div className={`${kit.row} ${styles.options}`}>
                  {m.options.map((o) => (
                    <button
                      key={o}
                      type="button"
                      aria-pressed={state[m.id].value === o}
                      className={`${kit.btn} ${styles.small} ${state[m.id].value === o ? kit.btnPrimary : ''}`}
                      onClick={() => choose(m.id, o)}
                    >
                      {o}
                    </button>
                  ))}
                </div>
              )}
            </div>
          ))}
          {MEMORIES.every((m) => state[m.id].removed) && <p className={kit.feedback}>No remembered preferences used.</p>}
        </div>

        <button type="button" className={styles.link} onClick={() => setShowAll((v) => !v)} aria-expanded={showAll}>
          {showAll ? 'Hide what I remember' : 'See everything I remember'}
        </button>

        {showAll && (
          <ul className={styles.allList}>
            {MEMORIES.map((m) => (
              <li key={m.id} className={state[m.id].removed ? styles.removedFact : undefined}>
                <span>{state[m.id].value}</span>
                <span className={styles.source}>{state[m.id].removed ? 'removed just now' : `${m.source} · ${m.learned}`}</span>
              </li>
            ))}
            {OTHER_FACTS.map((f) => (
              <li key={f.fact}>
                <span>{f.fact}</span>
                <span className={styles.source}>
                  {f.source} · {f.learned}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>

      <p className={kit.feedback} aria-live="polite">{feedback}</p>
    </div>
  );
}
